/**
 * Per-platform render budgets + the dev budget check (WR.3.4) — the pure,
 * node-safe core. Like plan.ts it imports NO `three` and does no I/O, so it runs
 * under `node --test` with no renderer (wr3-asset-pipeline-standup.md §12 risk 1).
 * The thin `SceneBuilder` (builder.ts) consumes this to assert the draw-call /
 * triangle budget in dev; THIS module is what the budget tests pin. The
 * distance-banded LOD + cull primitives (which operate on `AssetEntry`/`LodBand`)
 * live in the asset layer (`assets/lod.ts`); this module is the scene-cost half.
 *
 * Scope (spec §5/§9): set + enforce the draw-call / VRAM / download budgets owed
 * since Phase 3. These are TECHNICAL/perf figures (a Development/Fable call),
 * never economy targets — nothing here touches economy/scarcity/progression/
 * monetisation, and it is off the per-tick path (it scores a render plan; it
 * reads no sim state and changes no wire/sim/collision code).
 *
 * Import boundary (AD-122): shared types via `@great-cloud/shared/client` only.
 */

import type { MarkerKind, ScenePlan } from './plan.js';
import { coverInputFromPlan, groundCoverCost, planGroundCover } from './groundCover.js';
import { heightfieldClippedArrays, heightfieldTriangles } from './terrain.js';
import { islandCost, lipRim } from './island.js';

// ---------------------------------------------------------------------------
// Per-platform budgets (spec §9). Provisional technical targets — tuned against
// the fixture pack here in WR.3.4; density over budget is solved by
// LOD/instancing/culling, NEVER by shrinking the gameplay world.
// ---------------------------------------------------------------------------

export type RenderTier = 'desktop' | 'mobile';

export interface RenderBudget {
  /** Draw calls per frame over the visible cell set. */
  drawCalls: number;
  /** Triangles per frame. */
  triangles: number;
  /** Resident GPU texture memory, MB (a manifest-time budget — see note). */
  textureMemoryMB: number;
  /** Initial cell download, MB (a manifest-time budget — see note). */
  initialDownloadMB: number;
}

/**
 * The confirmed WR.3.4 budget table (spec §9). `drawCalls`/`triangles` are
 * asserted at runtime by `checkBudget` (the scene is fully known client-side);
 * `textureMemoryMB`/`initialDownloadMB` are manifest-time budgets a delivered
 * pack is measured against at build time (the fixture carries no real texture
 * bytes), kept here so the four live in one place.
 */
export const RENDER_BUDGETS: Readonly<Record<RenderTier, RenderBudget>> = {
  desktop: { drawCalls: 1500, triangles: 3_000_000, textureMemoryMB: 512, initialDownloadMB: 8 },
  mobile: { drawCalls: 500, triangles: 750_000, textureMemoryMB: 192, initialDownloadMB: 4 },
};

// ---------------------------------------------------------------------------
// Scene-cost estimate + budget check (spec §5: "a lightweight budget check —
// warn on draw-call/triangle overage — the scene builder can assert in dev").
// The estimate MIRRORS the builder's realization policy: dense scatter is one
// InstancedMesh (1 draw call); sparse markers stay individual (per-marker
// upgrade). The dev collision overlay is a debug aid, gated off in production,
// and is NOT counted.
// ---------------------------------------------------------------------------

/** Approximate triangle counts for the placeholder primitives (their three.js defaults). */
export const PRIMITIVE_TRIANGLES: Readonly<Record<'groundPlane' | 'rock' | MarkerKind, number>> = {
  groundPlane: 2, // PlaneGeometry(80, 80), 1 segment
  rock: 36, // DodecahedronGeometry(1)
  shrine: 12, // ConeGeometry(0.5, 2.2, 6)
  transition: 384, // TorusGeometry(1.1, 0.16, 8, 24)
  poi: 32, // CylinderGeometry(0.22, 0.22, 1.6, 8)
};

/**
 * The sky (AD-651 item 2 / AD-9007): three's Sky/SkyMesh dome — a unit BoxGeometry, 12
 * triangles, ONE draw on every tier; the sun disc and the cloud layer are in its shader. Its
 * real cost is the fragment work on visible sky pixels (it draws after the opaque world, so
 * covered pixels are depth-rejected), which draw counts can't see; mobile runs one fewer
 * cloud octave.
 */
export const SKY_DOME_TRIANGLES = 12;

export interface SceneCost {
  drawCalls: number;
  triangles: number;
}

export interface EstimateOptions {
  /** Instance the dense scatter into one draw call (default true — the WR.3.4 win). */
  instanceScatter?: boolean;
  /**
   * Count the E3.3.S5 ground cover this tier would draw (worst case: every chunk in view).
   * Absent ⇒ not counted — the tier-agnostic estimate stays the scene's own structure.
   */
  groundCoverTier?: RenderTier;
}

/**
 * Estimate the per-frame draw-call + triangle cost of a `ScenePlan`, mirroring
 * the builder: ground plane (+ grid lines), the scatter props as ONE
 * InstancedMesh, each client-local asset placement as one loaded object, and
 * each marker individually. Coarse by design — a dev guard, not a profiler.
 * Upgraded pack assets replace placeholders at load time and carry their own
 * measured cost (manifest budgets); this estimates the scene structure the
 * builder draws.
 */
export function estimateSceneCost(plan: ScenePlan, opts: EstimateOptions = {}): SceneCost {
  const instanceScatter = opts.instanceScatter ?? true;
  let drawCalls = 0;
  let triangles = 0;

  // Ground plane (+ grid lines ≈ 0 triangles, 1 draw). AD-408: a heightfield
  // ground is still ONE draw, at two triangles per raster cell.
  // AD-840: an island zone draws its island (top, underside, crags, roots, distant
  // islands, cloud sea) INSTEAD of the rectangle; lite on the mobile tier.
  if (plan.island) {
    // A relief island's top is the heightfield cut to the lip rim (builder.ts).
    const hf = plan.ground.heightfield;
    const { cx, cz } = plan.island.outline;
    const topTriangles = hf
      ? heightfieldClippedArrays(hf, lipRim(plan.island), { x: cx, z: cz }).indices.length / 3
      : undefined;
    const island = islandCost(
      plan.island,
      { lite: opts.groundCoverTier === 'mobile' },
      topTriangles,
    );
    drawCalls += island.drawCalls;
    triangles += island.triangles;
  } else {
    drawCalls += 1;
    triangles += plan.ground.heightfield
      ? heightfieldTriangles(plan.ground.heightfield)
      : PRIMITIVE_TRIANGLES.groundPlane;
  }
  if (plan.ground.grid) drawCalls += 1;

  // Dense scatter → one InstancedMesh (the WR.3.4 perf increment) sharing the rock geometry.
  if (plan.props.length > 0) {
    drawCalls += instanceScatter ? 1 : plan.props.length;
    triangles += plan.props.length * PRIMITIVE_TRIANGLES.rock;
  }

  // Client-local showcase placements are sparse loaded assets.
  drawCalls += plan.assetPlacements.length;

  // Biome dressing (AD-315) — settlement kit pieces + derived scatter. The
  // builder clones one template per placement, so each placement is a draw;
  // counting them here is what makes the per-rule scatter caps answerable to
  // the tier budget instead of being an unpoliced content knob.
  drawCalls += plan.dressing.length;

  // Sparse markers stay individual (per-marker upgrade path).
  for (const marker of plan.markers) {
    drawCalls += 1;
    triangles += PRIMITIVE_TRIANGLES[marker.kind];
  }

  // The sky dome — clouds and sun disc included (absent ⇒ the flat clear colour, free).
  if (plan.sky) {
    drawCalls += 1;
    triangles += SKY_DOME_TRIANGLES;
  }

  // E3.3.S5 ground cover (AD-159): one InstancedMesh per non-empty chunk.
  if (opts.groundCoverTier) {
    const cover = planGroundCover(coverInputFromPlan(plan), opts.groundCoverTier);
    if (cover) {
      const coverCost = groundCoverCost(cover);
      drawCalls += coverCost.drawCalls;
      triangles += coverCost.triangles;
    }
  }

  return { drawCalls, triangles };
}

export interface BudgetWarning {
  metric: 'drawCalls' | 'triangles';
  tier: RenderTier;
  actual: number;
  budget: number;
}

/** Compare a scene cost against a tier budget; `[]` ⇒ within budget. */
export function checkBudget(cost: SceneCost, tier: RenderTier): BudgetWarning[] {
  const budget = RENDER_BUDGETS[tier];
  const warnings: BudgetWarning[] = [];
  if (cost.drawCalls > budget.drawCalls) {
    warnings.push({ metric: 'drawCalls', tier, actual: cost.drawCalls, budget: budget.drawCalls });
  }
  if (cost.triangles > budget.triangles) {
    warnings.push({ metric: 'triangles', tier, actual: cost.triangles, budget: budget.triangles });
  }
  return warnings;
}

/** Estimate + check in one call (the form the scene builder asserts in dev). */
export function budgetWarnings(
  plan: ScenePlan,
  tier: RenderTier,
  opts?: EstimateOptions,
): BudgetWarning[] {
  return checkBudget(estimateSceneCost(plan, { groundCoverTier: tier, ...opts }), tier);
}

/** One-line dev message for a budget warning. */
export function describeBudgetWarning(warning: BudgetWarning): string {
  return `${warning.metric} ${warning.actual} exceeds the ${warning.tier} budget of ${warning.budget}`;
}
